import { useRef, useMemo } from 'react';
import { css } from '@emotion/react';
import { theme } from '@frontend/theme';
import { RadioField, Text, useControlledField } from '@frontend/design-system';
import { AxisGroup } from './types';

export const RadioGroup = ({
  group,
  draftY,
  onCheckboxChange,
  disabledOptions = [],
}: {
  group: AxisGroup;
  draftY: string[];
  onCheckboxChange: (values: string[]) => void;
  disabledOptions?: string[];
}) => {
  const draftYRef = useRef(draftY);
  draftYRef.current = draftY;

  const groupValueKey = group.options.map((o) => o.value).join(',');
  const groupValues = useMemo(() => group.options.map((o) => o.value), [groupValueKey]);

  const groupValuesRef = useRef(groupValues);
  groupValuesRef.current = groupValues;

  const selectedInGroup = useMemo(() => draftY.find((v) => groupValues.includes(v)) || '', [draftY, groupValues]);

  const radioProps = useControlledField({
    type: 'radio',
    value: selectedInGroup,
    onChange: (newGroupSelection) => {
      const currentDraftY = draftYRef.current;
      const currentGroupValues = groupValuesRef.current;
      const otherGroupSelections = currentDraftY.filter((v) => !currentGroupValues.includes(v));
      onCheckboxChange(newGroupSelection ? [...otherGroupSelections, newGroupSelection] : otherGroupSelections);
    },
  });

  return (
    <RadioField {...radioProps} name={group.label} css={styles.radioField}>
      {group.options.map((option) => (
        <RadioField.Radio
          key={option.value}
          value={option.value}
          css={{ display: 'flex', alignItems: 'center', marginBottom: theme.spacing(0) }}
          disabled={disabledOptions.includes(option.value)}
        >
          <Text size='large'>{option.label}</Text>
        </RadioField.Radio>
      ))}
    </RadioField>
  );
};

const styles = {
  radioField: css({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  }),
};
